import React, { useState, useRef, useEffect } from 'react';

export const Faqs = () => {
  const [openFaqId, setOpenFaqId] = useState(null);
  const [activeCategory, setActiveCategory] = useState("General");
  
  // Refs and State for Scroll Arrows
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const toggleFaq = (id) => {
    setOpenFaqId(openFaqId === id ? null : id);
  };

  // Scroll function with offset
  const scrollToCategory = (category) => {
    setActiveCategory(category);
    const element = document.getElementById(category);
    if (element) {
      const yOffset = -120; // Adjusted for fixed header if any
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({top: y, behavior: 'smooth'});
    }
  };

  // Scroll Handler Logic
  const scrollCategories = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 200; // How much to scroll per click
      scrollContainerRef.current.scrollBy({ 
        left: direction === 'left' ? -scrollAmount : scrollAmount, 
        behavior: 'smooth' 
      });
    }
  };

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  // Initial check on mount
  useEffect(() => {
    checkScrollButtons();
    window.addEventListener('resize', checkScrollButtons);
    return () => window.removeEventListener('resize', checkScrollButtons);
  }, []);

  const categories = [
    "General",
    "Troubleshooting",
    "Send Bulk SMS",
    "TRAI Regulations",
    "SMS Attachments",
    "Receive SMS",
    "SMS API Integration",
    "Support",
    "Industry-Specific",
    "Pricing"
  ];

  const faqData = [
    // --- GENERAL ---
    { id: 1, category: "General", question: "What is Eurofox Bulk SMS?", answer: "Eurofox is a powerful bulk SMS service provider allowing businesses to send promotional and transactional messages to thousands of customers instantly." },
    { id: 2, category: "General", question: "Do I need to install any software?", answer: "No, Eurofox is completely cloud-based. You can access your dashboard from any web browser without installing anything." },
    { id: 3, category: "General", question: "How long does it take to deliver SMS?", answer: "Our high-throughput routes ensure that messages are delivered within 5-10 seconds on average, depending on network congestion." },
    { id: 4, category: "General", question: "Is my data secure with Eurofox?", answer: "Yes, we use 256-bit SSL encryption and strict data privacy protocols to ensure your contact lists and message history are never shared." },
    { id: 5, category: "General", question: "What is the difference between Transactional and Promotional SMS?", answer: "Promotional SMS is for marketing (blocked on DND numbers), while Transactional SMS is for alerts like OTPs (delivered to all numbers 24/7)." },
    { id: 6, category: "General", question: "Can I send SMS in regional languages?", answer: "Yes, Eurofox supports Unicode, allowing you to send messages in Hindi, Marathi, Tamil, and other regional languages." },
    { id: 7, category: "General", question: "Is there a limit to how many SMS I can send?", answer: "There is no upper limit. You can send anywhere from 100 to 10 million SMS per day depending on your credit balance." },
    { id: 8, category: "General", question: "How do I create an account?", answer: "Click on the 'Sign Up' button, verify your mobile number and email, and your account will be active instantly with free test credits." },
    {
      id: 9,
      category: "General",
      question: "What is Sender ID and does it work?",
      answer: (
        <>
          A Sender ID (also called Header) is a 6-character alphabet name that appears as the sender of the SMS.
          <br />
          <br />
          It must be approved on the DLT portal and mapped in SMSGatewayHub before usage.
        </>
      ),
    },
    { id: 10, category: "General", question: "Can I schedule SMS for later?", answer: "Yes, our dashboard allows you to pick a specific date and time to automatically blast your campaign." },

    // --- TROUBLESHOOTING ---
    { id: 11, category: "Troubleshooting", question: "Why is my DLT template getting rejected?", answer: "Rejections usually happen due to variable format errors (e.g., usage of '{#var#}') or if the brand name is missing in the content." },
    { id: 12, category: "Troubleshooting", question: "Why are my messages showing 'Failed' status?", answer: "This often occurs if the destination number is invalid, switched off, or if your DLT template ID is incorrect." },
    { id: 13, category: "Troubleshooting", question: "I forgot my password, how do I reset it?", answer: "Click 'Forgot Password' on the login page. We will send a reset link to your registered email address." },
    { id: 14, category: "Troubleshooting", question: "Why is the API returning an authentication error?", answer: "Check if your API Key is correct and that you have whitelisted your server IP address in the security settings." },
    { id: 15, category: "Troubleshooting", question: "Why was my account suspended?", answer: "Accounts are suspended if we detect spamming, phishing links, or content that violates TRAI regulations." },

    // --- SEND BULK SMS ---
    { id: 16, category: "Send Bulk SMS", question: "What file formats do you accept for uploading contacts?", answer: "We support CSV, Excel (XLS/XLSX), and TXT files for bulk contact uploads." },
    { id: 17, category: "Send Bulk SMS", question: "Can I personalize messages with names?", answer: "Yes, using our 'Dynamic Fields' feature, you can insert specific names or variables from your uploaded excel sheet into the message." },
    { id: 18, category: "Send Bulk SMS", question: "How do I create a sender ID?", answer: "Go to 'Manage Senders' in your dashboard. Note that your 6-character Sender ID must match what you approved on the DLT platform." },
    { id: 19, category: "Send Bulk SMS", question: "Can I exclude DND numbers automatically?", answer: "Yes, for Promotional routes, our system automatically filters out DND (Do Not Disturb) numbers before sending so you don't waste credits." },
    { id: 20, category: "Send Bulk SMS", question: "How can I track the performance of my campaign?", answer: "Our 'Reports' section gives you real-time data on Delivered, Failed, and Click-through rates for your campaigns." },

    // --- TRAI REGULATIONS ---
    { id: 21, category: "TRAI Regulations", question: "What is DLT and why do I need it?", answer: "Distributed Ledger Technology (DLT) is a blockchain-based system mandated by TRAI to prevent SMS spam and fraud." },
    { id: 22, category: "TRAI Regulations", question: "Which DLT portals can I register on?", answer: "You can register on any operator platform like Jio, Vodafone (Vi), Airtel, or Tata Tele Services. The registration is valid across all operators." },
    { id: 23, category: "TRAI Regulations", question: "What documents are required for DLT?", answer: "You typically need your Business PAN Card, GST Certificate, Identity Proof of Authorized Signatory, and an Authorization Letter." },
    { id: 24, category: "TRAI Regulations", question: "Is there a fee for DLT registration?", answer: "Yes, telecom operators charge a fee (usually around ₹5900) for registration, though this varies by operator." },
    { id: 25, category: "TRAI Regulations", question: "What happens if I don't register on DLT?", answer: "If you are not registered, your SMS traffic will be blocked by the telecom operators, and you cannot send messages." },

    // --- SMS ATTACHMENTS ---
    { id: 26, category: "SMS Attachments", question: "Can I attach a PDF invoice?", answer: "You cannot directly attach files to SMS, but you can upload the PDF to our 'Files' section, generate a link, and send that link in the SMS." },
    { id: 27, category: "SMS Attachments", question: "Do you provide link tracking?", answer: "Yes, when you use our short-link tool for attachments, we track exactly who clicked the link and at what time." },
    { id: 28, category: "SMS Attachments", question: "Is there a size limit for files?", answer: "Yes, the maximum file size for upload via our 'Smart Link' feature is 10MB per file." },
    { id: 29, category: "SMS Attachments", question: "Can I send images in bulk?", answer: "Yes, similiar to documents, you can upload marketing images and send them as a clickable link in your bulk campaign." },
    { id: 30, category: "SMS Attachments", question: "Will the link consume SMS characters?", answer: "Yes, our short links are usually 20-22 characters long. Please account for this when drafting your 160-character message." },

    // --- RECEIVE SMS ---
    { id: 31, category: "Receive SMS", question: "What is a Long Code?", answer: "A Long Code is a standard 10-digit virtual mobile number used to receive SMS replies from customers (e.g., 'Text YES to 999xxxxx')." },
    { id: 32, category: "Receive SMS", question: "Can I set up auto-replies?", answer: "Yes, you can configure keywords (e.g., 'OFFER') that trigger an automatic SMS response when a customer texts your number." },
    { id: 33, category: "Receive SMS", question: "How do I view received messages?", answer: "All incoming messages appear in your 'Inbox' on the dashboard and can also be forwarded to your email or API URL." },
    { id: 34, category: "Receive SMS", question: "What is the cost of a Virtual Number?", answer: "Virtual Numbers (VMN) are rented on a monthly or yearly basis. Please contact sales for current inventory prices." },
    { id: 35, category: "Receive SMS", question: "Can I use my own sim card number?", answer: "No, bulk SMS receiving requires a cloud-hosted Virtual Number provided by us to handle high volumes and API forwarding." },

    // --- SMS API INTEGRATION ---
    { id: 36, category: "SMS API Integration", question: "Which languages does your API support?", answer: "Our REST API is platform-independent and works with PHP, Python, Java, Node.js, Ruby, C#, and more." },
    { id: 37, category: "SMS API Integration", question: "Where can I find the API Key?", answer: "Log in to your dashboard, navigate to 'Settings' > 'API Keys' to generate or view your unique authentication key." },
    { id: 38, category: "SMS API Integration", question: "Do you support Webhooks?", answer: "Yes, you can configure a Webhook URL (Callback URL) to receive real-time delivery reports directly to your server." },
    { id: 39, category: "SMS API Integration", question: "Is there a rate limit on the API?", answer: "Our standard API allows up to 500 requests per second. For higher limits, please contact our enterprise support." },
    { id: 40, category: "SMS API Integration", question: "How do I test the API?", answer: "We provide a Postman Collection and code samples in our documentation to help you test the endpoints quickly." },

    // --- SUPPORT ---
    {
      id: 41,
      category: "Support",
      question: "What customer support do you provide?",
      answer: (
        <>
          <p className="mb-3">We offer comprehensive support including:</p>
          <ul className="space-y-2">
            {[
              "24x7 technical email support",
              "Call support b/w 10 AM - 6 PM via support tickets",
              "Live chat support",
              "Dedicated account manager for enterprise clients",
              "API integration assistance with code samples",
              "DLT registration guidance and troubleshooting",
              "Dashboard training for new users",
              "Bulk upload assistance for large contact lists",
              "Priority support for urgent campaigns"
            ].map((item, index) => (
              <li key={index} className="flex items-start">
                <svg 
                  className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                </svg>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 font-medium text-gray-800">
            Our typical response time is under 30 minutes during business hours and under 2 hours for 24x7 support tickets.
          </p>
        </>
      )
    },   
    { id: 42, category: "Support", question: "Do you provide dedicated account managers?", answer: "Yes, enterprise clients with high volume commitments are assigned a dedicated Key Account Manager." },
    { id: 43, category: "Support", question: "How do I raise a ticket?", answer: "You can raise a support ticket directly from the 'Help' section in your dashboard or email us at support@eurofox.com." },

    // --- INDUSTRY SPECIFIC ---
    { id: 44, category: "Industry-Specific", question: "Is this suitable for Schools?", answer: "Yes, schools use Eurofox extensively for attendance alerts, exam results, and fee reminders." },
    { id: 45, category: "Industry-Specific", question: "Can Real Estate agents use this?", answer: "Absolutely. Real estate agents use our service to send property listings and open house invites to prospective buyers." },
    { id: 46, category: "Industry-Specific", question: "Do you support OTPs for E-commerce?", answer: "Yes, our high-priority Transactional routes are optimized for sub-5-second OTP delivery for e-commerce logins." },

    // --- PRICING ---
    { id: 47, category: "Pricing", question: "What is the cost per SMS?", answer: "Pricing follows a sliding scale volume model—the more you buy, the lower the cost per SMS. Check our pricing table for tiers." },
    { id: 48, category: "Pricing", question: "Is there a setup fee?", answer: "No, there are no setup fees or hidden charges. You only pay for the SMS credits you purchase." },
    { id: 49, category: "Pricing", question: "Do you offer refunds?", answer: "We offer a 7-day money-back guarantee if you are not satisfied with our service, subject to usage terms." },
    { id: 50, category: "Pricing", question: "What payment methods do you accept?", answer: "We accept multiple payment methods including online payment (credit/debit cards, UPI, net banking), bank transfer/NEFT/RTGS, digital wallets (Paytm, PhonePe, Google Pay), and for enterprise clients, invoice-based payments with credit terms." },
  ];

  return (
    <div className="w-full px-4 py-16 bg-white min-h-screen font-sans text-left">
      
      {/* 1. Header Section */}
      <div className="text-center mb-16 max-w-6xl mx-auto">
        <div className="inline-block mb-4">
          <span className="text-sm font-semibold uppercase tracking-wider text-[#F46653] bg-[#FFF5F3] px-4 py-2 rounded-full">
            FAQ
          </span>
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
          Frequently Asked Questions
        </h2>
        <p className="text-gray-600 text-lg max-w-2xl mx-auto">
          Everything you need to know about Eurofox Bulk SMS. Can't find the answer you're looking for? 
          <a href="#contact" className="text-[#0C4550] font-medium hover:text-[#F46653] transition-colors ml-1">
            Contact our support team
          </a>.
        </p>
      </div>

      {/* 2. Category Navigation */}
      <div className="max-w-6xl mx-auto mb-16 relative">
        <div className="flex flex-col items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Browse by Category</h3>
          
          <div className="w-full relative px-12">
            {showLeftArrow && (
              <button 
                onClick={() => scrollCategories('left')}
                className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 p-2 rounded-full bg-white border border-gray-200 shadow-md text-gray-600 hover:text-[#0C4550] hover:scale-110 transition-all duration-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            <div 
              ref={scrollContainerRef}
              onScroll={checkScrollButtons}
              className="flex overflow-x-auto pb-4 hide-scrollbar scroll-smooth"
            >
              <div className="flex space-x-3 px-2">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => scrollToCategory(category)}
                    className={`
                      px-6 py-3 rounded-lg text-sm font-medium whitespace-nowrap
                      transition-all duration-300 ease-out
                      ${activeCategory === category 
                        ? 'bg-[#0C4550] text-white shadow-md' 
                        : 'bg-white text-gray-800 hover:bg-gray-50 border border-gray-200'
                      }
                    `}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {showRightArrow && (
              <button 
                onClick={() => scrollCategories('right')}
                className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 p-2 rounded-full bg-white border border-gray-200 shadow-md text-gray-600 hover:text-[#0C4550] hover:scale-110 transition-all duration-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 3. FAQ Content */}
      <div className="max-w-5xl mx-auto">
        {categories.map((category) => {
          const categoryQuestions = faqData.filter(q => q.category === category);
          
          return (
            <div key={category} id={category} className="scroll-mt-32 mb-16 last:mb-0">
              <div className="pb-6 mb-6 text-left border-b border-gray-200">
                <h3 className="text-3xl font-medium text-black flex items-center">
                  <span className="mr-3 text-[#0C4550]">#</span>
                  {category}
                  <span className="ml-4 text-sm font-normal text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                    {categoryQuestions.length} questions
                  </span>
                </h3>
              </div>

              <div className="border border-gray-200 rounded-xl bg-white overflow-hidden divide-y divide-gray-200">
                {categoryQuestions.map((faq) => {
                  const isOpen = openFaqId === faq.id;
                  
                  return (
                    <div key={faq.id} className={`transition-colors duration-300 ${isOpen ? 'bg-gray-50' : 'hover:bg-gray-50'}`}>
                      <button
                        onClick={() => toggleFaq(faq.id)}
                        className="w-full text-left p-6 flex justify-between items-center focus:outline-none"
                      >
                        <div className="flex items-center w-full">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-4 flex-shrink-0 transition-colors duration-300 ${isOpen ? 'bg-[#0C4550] text-white' : 'bg-gray-100 text-gray-600'}`}>
                            <span className="text-sm font-medium">{String(faq.id).padStart(2, '0')}</span>
                          </div>
                          <h4 className="text-medium font-medium text-black pr-8">{faq.question}</h4>
                        </div>
                        <span className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-transform duration-300 ml-4 ${isOpen ? 'bg-[#F46653] text-white rotate-180' : 'bg-gray-100 text-gray-600'}`}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </span>
                      </button>

                      <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                        <div className="overflow-hidden">
                          <div className="px-14 pb-6 text-gray-700 leading-relaxed text-left">
                            <div className="pl-4 border-l-2 border-[#F46653]">
                              {faq.answer}
                            </div>
                            <div className={`mt-4 flex items-center text-sm text-gray-500 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`}>
                              <span className="inline-flex items-center mr-4">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path></svg>
                                Need more help?
                              </span>
                              <a href="#contact" className="text-[#0C4550] font-medium hover:text-[#F46653] transition-colors flex items-center">
                                Contact support
                                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <style jsx>{`
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
};